import { Alert, Button, Form, Input, Modal, Space } from "antd";
import EnvironmentSelect from "../template-modules/EnvironmentSelect";
import { EnvironmentDetails } from "../../types";
import AwsAccountSelect from "../AwsAccountSelect";
import { useEffect, useState } from "react";
import { RequiredMark } from "antd/lib/form/Form";
import { getCloudService, getEnvironmentService } from "../../backend";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import { usePrevious } from "../../contexts/State";
import { AWS_REGIONS } from "../../enums/enums";
import { GetStageResourcesResponseResources } from "@microtica/ms-engine-sdk";

export interface CreateClusterModalProps {
    envId?: string;
    modalVisible?: boolean;
    setModalVisible: (visible: boolean) => void;
    selectedEnv?: EnvironmentDetails;
    setSelectedEnv: React.Dispatch<React.SetStateAction<EnvironmentDetails | undefined>>;
    clusterAppName?: string;
    setClusterAppName: (clusterAppName: string) => void;
    onOk?: () => void;
    onCancel?: () => void;
}

const CreateClusterModal = ({
    modalVisible,
    setModalVisible,
    selectedEnv,
    setSelectedEnv,
    clusterAppName,
    setClusterAppName,
    onOk,
    onCancel
}: CreateClusterModalProps) => {
    const currentProject = useRecoilValue(currentProjectState);
    const [form] = Form.useForm();
    const [requiredMark] = useState<RequiredMark>('optional');
    const [awsAccounts, setAwsAccounts] = useState<{ id: string; name: string }[]>([]);
    const prevEnvironment = usePrevious(selectedEnv);
    const [missingAwsConfigs, setMissingAwsConfigs] = useState(false);
    const [stageResources, setStageResources] = useState<GetStageResourcesResponseResources[]>([]);

    useEffect(() => {
        const loadData = async () => {
            if (selectedEnv) {
                if (selectedEnv.id !== prevEnvironment?.id) {
                    const { data: { awsAccountId, region } } = await getEnvironmentService().getStage(
                        selectedEnv.id,
                        currentProject!.id
                    );
                    if (awsAccountId && region) {
                        setMissingAwsConfigs(false);
                    } else {
                        await loadAwsAccounts();
                        setMissingAwsConfigs(true);
                    }
                }

                // these resources are used to check whether the cluster's name matches one of them
                const { data: { resources } } = await getEnvironmentService().getStageResources(selectedEnv.id!, currentProject!.id);
                setStageResources(resources);
            };
        };

        loadData();
    }, [selectedEnv]);

    const loadAwsAccounts = async () => {
        const { data: { awsAccounts } } = await getCloudService().getAwsAccounts(currentProject!.id);
        setAwsAccounts(awsAccounts.map(acc => ({ id: acc.id, name: acc.accountName })));
    }

    return (
        <Modal
            title="Create Amazon EKS Cluster"
            centered
            closable={false}
            maskClosable={false}
            visible={modalVisible}
            footer={[
                <Button
                    key="cancel"
                    onClick={() => {
                        setModalVisible(false);
                        onCancel?.();
                    }}
                >
                    Cancel
                </Button>,
                <Button
                    htmlType="submit"
                    type="primary"
                    key="continue"
                    disabled={!selectedEnv?.accountId || !selectedEnv?.region}
                    onClick={async () => {
                        await form.validateFields();
                        setModalVisible(false);
                        onOk?.();
                    }}
                >
                    Continue
                </Button>
            ]}
            onCancel={() => {
                setModalVisible(false);
                onCancel?.();
            }}
        >
            <Form
                form={form}
                layout="horizontal"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14, offset: 1 }}
                labelAlign="right"
                requiredMark={requiredMark}
            >
                <Space direction="vertical" size="large">
                    <Alert
                        message="By continuing the Kubernetes cluster will be deployed first and then your app. The cluster creation could take up to 15 mins."
                    />
                    <EnvironmentSelect
                        selectedEnv={selectedEnv}
                        onSelect={(env) => {
                            setSelectedEnv({
                                ...env,
                                region: AWS_REGIONS.find(r => r.value === env?.region)?.id
                            } as EnvironmentDetails
                            )
                        }}
                    />
                    {
                        selectedEnv && missingAwsConfigs &&
                        <AwsAccountSelect
                            selectedAccount={selectedEnv}
                            onSelect={({ accountId, region }) => {
                                setSelectedEnv(env => ({
                                    ...env,
                                    accountId,
                                    region
                                }) as EnvironmentDetails)
                            }}
                            awsAccounts={awsAccounts}
                        />
                    }
                    <Form.Item
                        key="clusterAppName"
                        name="clusterAppName"
                        label="Cluster name"
                        initialValue={clusterAppName}
                        rules={[
                            { required: true, message: "Enter cluster name" },
                            { min: 3, message: 'Cluster name must be minimum 3 characters.' },
                            { max: 15, message: 'Cluster name must be maximum 15 characters.' },
                            { pattern: new RegExp(/^[A-Za-z0-9]+$/), message: "Cluster name must contain only alphanumeric characters." },
                            {
                                validator: async (_, value) => {
                                    if (selectedEnv) {
                                        // check if resource with the same name already exists
                                        // the check must be case insesitive due to db primary key restrictions
                                        const existingResource = stageResources.find(r => r.name.toLowerCase() === value.toLowerCase());
                                        if (existingResource) {
                                            throw new Error(`Resource with the same name already exists in your environment: ${existingResource.name}`);
                                        }
                                    }
                                },
                            }
                        ]}
                        tooltip="The name of the cluster (e.g. EKS). The name will be shown in the portal."
                    >
                        <Input
                            type={"text"}
                            onChange={(event) => setClusterAppName(event.target.value)}
                            placeholder="EKS"
                        />
                    </Form.Item>
                </Space>
            </Form>
        </Modal>
    )
}

export default CreateClusterModal;